import clsx from 'clsx';
import * as styles from '../styles/containers/CustomerEditor.less';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { withTranslation, WithTranslation } from "react-i18next";
import { DASHBOARD_URL } from '../constants/config';
import Error from '../components/common/Error';
import Loader from '../components/common/Loader';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import SuperuserIsActive from './SuperuserIsActive';
import { useActions } from '../hooks/useActions';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store/configureStore';
import type { EntityId } from '../types/models';

interface CustomerOption {
  value: EntityId;
  label: string;
}

interface CustomerEditorOwnProps {
  className?: string;
}

//-------------------------------------------------------------
// Update loading status based on request reducer store updates.
// If any request type in the list is true, loading is true.
//-------------------------------------------------------------
const isLoadingByList = (_store: RootState, _list: string[]): boolean => {
  for (let i = 0, ilen = _list.length; i < ilen; i++) {
    if (_store.loading[_list[i]] === true) {
      return true;
    }
  }
  return false;
};

const dashboardLoadingList = [
  'REQUEST_USER_CUSTOMERS',
  'REQUEST_USER_CUSTOMER',
];

const CustomerEditor = (props: CustomerEditorOwnProps & WithTranslation) => {
  const { t } = props; // [t, i18n]
  const actions = useActions();
  const params = useParams();
  const navigate = useNavigate();

  const dashboardIsLoading = useAppSelector((store) => isLoadingByList(store, dashboardLoadingList));
  const error = useAppSelector((store) => store.customer.error);
  const errorMessage = useAppSelector((store) => store.customer.errorMessage);
  const customerName = useAppSelector((store) => store.customer.customerName || '');
  const email = useAppSelector((store) => store.customer.email);
  const disabled = useAppSelector((store) => store.customer.disabled);
  const isCustomerValid = useAppSelector((store) => store.customer.isCustomerValid || false);
  const isCustomerDeleting = useAppSelector((store) => store.customer.isCustomerDeleting);
  const isAuthenticated = useAppSelector((store) => store.auth.isAuthenticated);
  const isSuperuser = useAppSelector((store) => store.auth.isSuperuser);

  const [showDialog, setShowDialog] = useState(false);
  // Affects the Save button's disabled state, so it must trigger a re-render
  // (was an instance field that never re-rendered — the stale-button bug).
  const [isCustomerNameUsed, setIsCustomerNameUsed] = useState(false);

  // Uncontrolled-input backing values (defaultValue/defaultChecked) + lookup data.
  // Set once from the fetch; the form only mounts after loading, so defaultValue
  // picks these up. Refs (not state) — mutating them must not re-render.
  const customerNameRef = useRef('');
  const emailRef = useRef('');
  const contactNameRef = useRef('');
  const disabledRef = useRef(false);
  const targetCustomersRef = useRef<CustomerOption[]>([]);

  const editCustomer = useMemo(() => debounce(actions.editCustomer, 250), [actions]);
  useEffect(() => () => editCustomer.cancel(), [editCustomer]);
  const fastEditCustomer = actions.editCustomer;

  // componentDidMount: load the customer (and the customer list).
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    actions.resetCustomer();
    console.log(isSuperuser ? 'Your role: Superuser' : 'Your role: Admin');
    const customerId = parseInt(params.customerId ?? "") || 0;
    if (customerId) {
      actions.fetchCustomer(customerId)
        .then((data: any) => {
          const customer = data.payload;
          customerNameRef.current = customer.name;
          emailRef.current = customer.email;
          disabledRef.current = customer.disabled;
          fastEditCustomer('customerName', customer.name, true); // true === skip validation
          fastEditCustomer('email', customer.email, true);
          fastEditCustomer('disabled', customer.disabled, true);
          actions.fetchUserCustomers()
            .then((customers: any) => {
              actions.validateCustomer();
              // Selection array for dropdown / name-collision check.
              targetCustomersRef.current = customers.payload.map((client: any) => ({
                value: client.id,
                label: client.name,
              }));
            });
        });
    } else {
      actions.fetchUserCustomers()
        .then((customers: any) => {
          targetCustomersRef.current = customers.payload.map((customer: any) => ({
            value: customer.id,
            label: customer.name,
          }));
        });
      console.info('Init new customer.');
    }
  }, [isAuthenticated, isSuperuser, params.customerId, actions, fastEditCustomer]);

  const onSaveCustomer = () => {
    const customerId = parseInt(params.customerId ?? "") || 0;
    const customer = {
      id: customerId, // If zero a new customer entry is created
      name: customerName.trim(),
      email: email.trim(),
      disabled: disabled,
      themes: '1',
    };
    const action = customerId
      ? actions.updateCustomer(customer)
      : actions.saveNewCustomer(customer);
    // On update we already know the id; on create, new-api returns it as `id`.
    action.then((response: any) => navigate('/customer/' + (customerId || response.id)));
  };

  const onChangeCustomerName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value.trim();
    customerNameRef.current = name;
    // Check that the name is not already used (new customers only).
    const item = targetCustomersRef.current.filter((x) => x.label === name);
    const customerId = parseInt(params.customerId ?? "") || 0;
    const element = document.getElementById('new-customer');
    if (!customerId && item.length > 0) {
      if (element) element.style.color = 'red';
      setIsCustomerNameUsed(true);
    } else {
      if (element) element.style.color = 'black';
      setIsCustomerNameUsed(false);
    }
    editCustomer('customerName', name.trim());
  };

  const onChangeContactName = (e: React.ChangeEvent<HTMLInputElement>) => editCustomer('contactName', e.target.value.trim());

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => editCustomer('email', e.target.value.trim());

  const onChangeDisabled = () => {
    const isDisabled = (document.getElementById('tickbox_disabled') as HTMLInputElement | null)?.checked === true;
    editCustomer('disabled', isDisabled);
    disabledRef.current = isDisabled;
  };

  const onHandleDeleteClick = () => setShowDialog(true);

  const onHandleConfirm = (value: string) => {
    if (!disabled) {
      return;
    }
    if (value === customerNameRef.current) {
      actions.deleteCustomer(customerNameRef.current)
        .then((response: any) => {
          if (response) {
            navigate('/');
          }
        });
      setShowDialog(false);
    } else {
      alert('Incorrect customer name.');
    }
  };

  const onHandleCancel = () => setShowDialog(false);

  const customerId = params.customerId || false;

  if (error) {
    return <Error message={errorMessage} />;
  }
  if (dashboardIsLoading) {
    return <Loader style={{ padding: "2em", textAlign: "center" }} className={props.className} />;
  }

  return (
    <div className={clsx(styles['customer--editor'], 'pure-u-24-24')} >
      <h2>{t('customer.editor.name')}
        {dashboardIsLoading &&
          <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
      </h2>
      <SuperuserIsActive>
        <label className={styles.label}>
          {t('main.customer')} <input id="new-customer"
            className={styles.input}
            type="text"
            placeholder="Customer name"
            defaultValue={customerNameRef.current}
            onChange={onChangeCustomerName}
          />
        </label>
      </SuperuserIsActive>
      <label className={styles.hidden}>
        {t('customer.editor.name')} <input
          className={styles.input}
          type="text"
          placeholder="Contact name"
          defaultValue={contactNameRef.current}
          onChange={onChangeContactName}
        />
        <a className={customerId ? styles['customer--link'] : styles.hidden}
          href={`${DASHBOARD_URL}?hash=${customerId}`}
          title="Open customer home page"
          target="_blank"><i className="fa fa-lg fa-external-link" aria-hidden="true"></i></a>
      </label>
      <label className={styles.label}>
        {t('customer.editor.email')} <input
          className={styles.input}
          type={'email'}
          placeholder="Customer email"
          defaultValue={emailRef.current}
          onChange={onChangeEmail}
        />
      </label>

      <div>
        <div className="pure-u-3-24"></div>
        <div className="pure-u-12-24">

          <div className="chartSelector">
            <div className="pure-u-1-24">
              <input
                id="tickbox_disabled"
                type={'checkbox'}
                title="Disable customer access"
                defaultChecked={disabledRef.current}
                onClick={onChangeDisabled}
              />
            </div>
            <div className="pure-u-10-24">
              <label className={styles.label}>{t('user.editor.disabled')}</label>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex" }}>
        <button className={clsx(styles.save, 'button success')}
          onClick={onSaveCustomer}
          disabled={!isCustomerValid || isCustomerNameUsed || isCustomerDeleting}
          >{t('button.save')}</button>
        <SuperuserIsActive>
          <button className={clsx(!disabled ? styles.hidden : styles['delete-button'], 'button alert')}
            onClick={onHandleDeleteClick}
            disabled={isCustomerDeleting}
            >{t('button.delete')}</button>

          {showDialog && (
            <ConfirmationDialog
              message={t('customer.editor.confirmDelete')}
              confirmText={t('button.yes')}
              cancelText={t('button.no')}
              showInput={true}
              inputLabel={t('customer.editor.typeCustomerName')}
              onConfirm={(value) => { onHandleConfirm(value) }}
              onCancel={onHandleCancel}
            />
          )}
        </SuperuserIsActive>
      </div>
    </div>
  );
};

export default withTranslation('common')(CustomerEditor);
